import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Gift, LogOut, Menu, Moon, Package, Search, ShoppingBag, ShoppingCart, Sun, User as UserIcon, Wallet, X,
} from 'lucide-react';
import { toast, useAuthStore, useCartStore, useFeature, useTenantStore, useThemeStore } from '@/app/stores';
import { Drawer } from '@/components/ui/Overlay';
import { Dropdown } from '@/components/ui/Dropdown';
import { ButtonLink } from '@/components/ui/Button';
import { Container } from '@/components/ui/Layout';
import { cartCount } from '@/lib/pricing';
import { initials } from '@/lib/format';
import { cn } from '@/lib/cn';
import { mainNav, type NavLinkDef } from './nav';
import { AllAppsLink } from '@/components/AllAppsLink';
import { SearchBox } from './SearchBox';

export function useVisibleNav(links: NavLinkDef[]) {
  const features = useTenantStore((s) => s.tenant.features);
  return links.filter((l) => !l.feature || features[l.feature]);
}

/** Header menu: the merchant's configured navigation (admin -> Navigation), filtered by enabled modules. */
export function useStoreNav(): NavLinkDef[] {
  const tenant = useTenantStore((s) => s.tenant);
  const fallback = useVisibleNav(mainNav);
  const items = tenant.navigation?.header;
  if (!items) return fallback;
  return items.filter((i) => i.enabled && (!i.feature || tenant.features[i.feature])).map((i) => ({ label: i.label, to: i.to }));
}

const isExternal = (to: string) => /^https?:\/\//.test(to);

export function StoreNavLink({ link, className, onClick }: { link: NavLinkDef; className: (active: boolean) => string; onClick?: () => void }) {
  if (isExternal(link.to)) return <a href={link.to} target="_blank" rel="noreferrer" onClick={onClick} className={className(false)}>{link.label}</a>;
  return <NavLink to={link.to} end={link.to === '/'} onClick={onClick} className={({ isActive }) => className(isActive)}>{link.label}</NavLink>;
}

export function AnnouncementBar() {
  const text = useTenantStore((s) => s.tenant.announcement);
  const [hidden, setHidden] = useState(false);
  if (!text || hidden) return null;
  return (
    <div className="bg-secondary text-secondary-foreground">
      <Container className="relative flex items-center justify-center py-2 pr-8 text-center text-xs sm:text-sm">
        <p>{text}</p>
        <button onClick={() => setHidden(true)} aria-label="Dismiss announcement" className="absolute right-4 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100">
          <X className="size-4" />
        </button>
      </Container>
    </div>
  );
}

export function Logo({ onDark }: { onDark?: boolean }) {
  const { name, logoUrl } = useTenantStore((s) => s.tenant);
  return (
    <Link to="/" className="flex items-center gap-2.5" aria-label={`${name} home`}>
      {logoUrl
        ? <img src={logoUrl} alt="" className="size-9 rounded-xl object-contain" />
        : <span className="grid size-9 place-items-center rounded-xl bg-primary text-primary-foreground shadow-glow"><ShoppingBag className="size-5" /></span>}
      <span className={cn('hidden whitespace-nowrap text-lg font-extrabold tracking-tight sm:block', onDark && 'text-white')}>{name}</span>
    </Link>
  );
}

const iconBtn = 'relative grid size-10 place-items-center rounded-lg text-foreground hover:bg-muted';

function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const links = useStoreNav();
  return (
    <Drawer open={open} onClose={onClose} side="left" title="Menu">
      <nav className="flex flex-col p-3">
        {links.map((l) => (
          <StoreNavLink key={l.label + l.to} link={l} onClick={onClose} className={(a) => cn('rounded-lg px-4 py-3 text-base font-semibold', a ? 'bg-primary-soft text-primary' : 'hover:bg-muted')} />
        ))}
      </nav>
    </Drawer>
  );
}

export function Header() {
  const nav = useNavigate();
  const links = useStoreNav();
  const modeToggle = useTenantStore((s) => s.tenant.theme.allowModeToggle !== false);
  const cartOn = useFeature('cart');
  const walletOn = useFeature('wallet');
  const loyaltyOn = useFeature('loyalty');
  const ordersOn = useFeature('orders');
  const count = useCartStore((s) => cartCount(s.lines));
  const { user, logout } = useAuthStore();
  const { mode, toggle } = useThemeStore();
  const [menu, setMenu] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/90 backdrop-blur-md">
      <Container className="flex h-16 items-center gap-2 sm:gap-4">
        <button className={cn(iconBtn, 'lg:hidden')} onClick={() => setMenu(true)} aria-label="Open menu"><Menu className="size-5" /></button>
        <Logo />
        <nav className="ml-2 hidden items-center lg:flex xl:ml-4 xl:gap-1" aria-label="Main">
          {links.map((l) => (
            <StoreNavLink key={l.label + l.to} link={l} className={(a) => cn('rounded-lg px-2.5 py-2 text-sm font-semibold transition-colors xl:px-3', a ? 'text-primary' : 'text-muted-foreground hover:text-foreground')} />
          ))}
        </nav>
        <SearchBox className="ml-auto hidden min-w-48 max-w-md flex-1 md:block" />
        <div className="ml-auto flex items-center gap-0.5 md:ml-0">
          <AllAppsLink className="mr-1 hidden h-9 md:inline-flex" />
          <button className={cn(iconBtn, 'md:hidden')} onClick={() => setMobileSearch((s) => !s)} aria-label="Search"><Search className="size-5" /></button>
          {modeToggle && (
            <button className={iconBtn} onClick={toggle} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {mode === 'dark' ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </button>
          )}
          {cartOn && (
            <Link to="/cart" className={iconBtn} aria-label={`Cart, ${count} items`}>
              <ShoppingCart className="size-5" />
              {count > 0 && (
                <span className="absolute right-0.5 top-0.5 grid min-w-4.5 place-items-center rounded-full bg-accent px-1 text-[10px] font-bold leading-4.5 text-accent-foreground">{count}</span>
              )}
            </Link>
          )}
          {user ? (
            <Dropdown
              label="Account menu"
              trigger={<span className="grid size-10 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{initials(user.name)}</span>}
              items={[
                { label: 'My account', to: '/account', icon: <UserIcon className="size-4" /> },
                ...(ordersOn ? [{ label: 'Orders', to: '/account/orders', icon: <Package className="size-4" /> }] : []),
                ...(walletOn ? [{ label: 'Wallet', to: '/account/wallet', icon: <Wallet className="size-4" /> }] : []),
                ...(loyaltyOn ? [{ label: 'Loyalty points', to: '/account/loyalty', icon: <Gift className="size-4" /> }] : []),
                {
                  label: 'Sign out', icon: <LogOut className="size-4" />, danger: true, divider: true,
                  onSelect: async () => { await logout(); toast.info('Signed out'); nav('/'); },
                },
              ]}
            />
          ) : (
            <>
              <ButtonLink to="/login" variant="ghost" size="sm" className="hidden 2xl:inline-flex">Sign in</ButtonLink>
              <ButtonLink to="/register" size="sm" className="hidden sm:inline-flex">Register</ButtonLink>
              <Link to="/login" className={cn(iconBtn, '2xl:hidden')} aria-label="Sign in"><UserIcon className="size-5" /></Link>
            </>
          )}
        </div>
      </Container>
      {mobileSearch && (
        <Container className="pb-3 md:hidden"><SearchBox autoFocus onDone={() => setMobileSearch(false)} /></Container>
      )}
      <MobileMenu open={menu} onClose={() => setMenu(false)} />
    </header>
  );
}

